import { autoinject } from "aurelia-framework";
import { I18N } from "aurelia-i18n";

@autoinject
export class LocaleService {
  /** cache for RegExps, grouped by locale and type ('float' or 'int') */
  private regexpMap: Map<String, Map<String, RegExp>> = new Map();

  constructor(private i18n: I18N) {}

  getDecimalMark(): string {
    return this.i18n.tr("numbers.decimalMark");
  }

  getNegativeSign() {
    return this.i18n.tr("numbers.negativeSign");
  }

  getGroupingSeparator() {
    return this.i18n.tr("numbers.groupingSeparator");
  }

  getTrueOrFalse(b: boolean) {
    return b
      ? this.i18n.tr("components.ag-grid.boolean.true")
      : this.i18n.tr("components.ag-grid.boolean.false");
  }

  /**
   * checks if the given string parses as a float, using decimal and
   * thousands separators of the current locale.
   * @param s the string
   * @returns {boolean} true if the string consists solely of a parsable float
   */
  isFloat(s: string | number): boolean {
    if (typeof s === "number") return true;
    return this.getRegExp("float").test(s);
  }

  /**
   * try to parse a string as float, using the decimal and thousands
   * separator of the current locale.
   *
   * @param s the string
   * @returns {number} either the parsed float, or {@code NaN}
   */
  parseFloat(s: string | number): number {
    if (typeof s === "number") return s;
    if (!this.isFloat(s)) return NaN;
    return parseFloat(this.toInternalFloat(s));
  }

  /**
   * checks if the given string parses as a integer, using decimal and
   * thousands separators of the current locale.
   * @param s the string
   * @returns {boolean} true if the string consists solely of a parsable integer
   */
  isInt(s: string): boolean {
    return this.getRegExp("int").test(s);
  }

  /**
   * try to parse a string as integer, using the decimal and thousands
   * separator from the current locale.
   *
   * @param s the string
   * @returns {number} either the parsed integer, or {@code NaN}
   */
  parseInt(s: string | number): number {
    if (typeof s === "number") return s;
    if (!this.isInt(s)) return NaN;
    return parseInt(this.toInternalFloat(s));
  }

  formatFloat(n: number, formatMask?: string): string {
    return this.formatNumber(n, formatMask || "float");
  }

  formatInt(n: number, formatMask?: string): string {
    return this.formatNumber(n, formatMask || "int");
  }

  formatNumber(n: number, formatMask: string): string {
    if (!formatMask) formatMask = "float";

    if (formatMask === "float") {
      // KRACHT-82: special treatment of decimal places after comma
      // convert the number to a string given the following heuristics:
      //         n <  0.01  : use 6 decimal places after comma
      // 0.01 <= n <  100   : use 2 decimal places
      // 100  <= n <  10000 : use 1 decimal places
      //         n >= 10000 : no decimal places after comma
      let numberOfDecimalPlaces;
      if (n < 0.01) numberOfDecimalPlaces = 6;
      else if (n < 100) numberOfDecimalPlaces = 2;
      else if (n < 10000) numberOfDecimalPlaces = 1;
      else numberOfDecimalPlaces = 0;
      formatMask = this.createFloatFormatMaskWithDecimalPlaces(
        numberOfDecimalPlaces,
      );
    } else if (formatMask === "int") {
      formatMask = this.i18n.tr("numbers.displaying.int");
    }

    // Note: currently this function ignores the position of the grouping separator 'G' because the Intl.NumberFormat doesn't provide this.
    // instead the grouping is done by Intl.NumberFormat
    const hasGrouping = formatMask.indexOf("G") >= 0;
    const decimalMarkPos = formatMask.indexOf("D");
    const zeroPos = formatMask.lastIndexOf("0");
    const maximumFractionDigits =
      decimalMarkPos >= 0 ? formatMask.length - decimalMarkPos - 1 : 0;
    const minimumFractionDigits =
      decimalMarkPos >= 0 && zeroPos > decimalMarkPos
        ? zeroPos - decimalMarkPos
        : 0;
    let result = new Intl.NumberFormat(this.i18n.getLocale(), {
      style: "decimal",
      useGrouping: hasGrouping,
      minimumIntegerDigits: 1,
      minimumFractionDigits: minimumFractionDigits,
      maximumFractionDigits: maximumFractionDigits,
    }).format(n);
    // make grouping character for locale "fr" pdf ready:
    // some languages use spaces as grouping separator
    // the NumberFormat.format() method sets them as " ",
    // what is displayed in the pdf report (see kracht-pumpen-report.ts) as not displayable rectangle
    // still works for html as well
    return result.replace(" ", " ");
  }

  private getRegExp(type: string): RegExp {
    const locale = this.i18n.getLocale();
    let localeMap = this.regexpMap.get(locale);
    if (!localeMap) {
      localeMap = new Map<String, RegExp>();
      this.regexpMap.set(locale, localeMap);
    }
    let regexp = localeMap.get(type);
    if (!regexp) {
      regexp = this.loadRegexp(type);
      localeMap.set(type, regexp);
    }
    return regexp;
  }

  private loadRegexp(type: string): RegExp {
    const decimalMark = this.getDecimalMark();
    const groupingSeparator = this.getGroupingSeparator();
    const negativeSign = this.getNegativeSign();
    const formatMask = this.i18n
      .tr(`numbers.parsing.${type}`)
      .replace(/#/g, "\\d")
      .replace(/0/g, "\\d")
      .replace(/N/g, "\\" + negativeSign)
      .replace(/G/g, "\\" + groupingSeparator)
      .replace(/D/g, "\\" + decimalMark);
    return new RegExp("^" + formatMask + "$");
  }

  /**
   * in a given string, replaces all locale-specific grouping separators,
   * decimal marks and minus signs with their english counterparts so that it
   * can be parsed with the system function {@code parseFloat()}.
   */
  private toInternalFloat(s: string): string {
    try {
      const decimalMark = this.getDecimalMark();
      const groupingSeparator = this.getGroupingSeparator();
      const negativeSign = this.getNegativeSign();
      return s
        .replace(this.escape(groupingSeparator), "")
        .replace(this.escape(decimalMark), ".")
        .replace(this.escape(negativeSign), "-");
    } catch (e) {
      console.log("toInternalfloat error with string '" + s + "'", e);
      return s;
    }
  }

  private escape(s: string): RegExp {
    return new RegExp(s.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"), "g");
  }

  private createFloatFormatMaskWithDecimalPlaces(
    numberOfDecimalPlaces: number,
  ): string {
    const formatMask = this.i18n.tr("numbers.displaying.float");
    if (numberOfDecimalPlaces > 0)
      return formatMask.replace(
        /D0*/,
        "D" + this.repeatString("0", numberOfDecimalPlaces),
      );
    else return formatMask.replace(/D0*/, "");
  }

  private repeatString(s: string, count: number): string {
    let r = "";
    for (let i = 0; i < count; i++) r += s;
    return r;
  }
}
